import React, { useCallback, useRef, useState } from 'react'
import { useScanKeyboard } from './useScanKeyboard'

interface UseScanInputArgs {
  /**
   * Se ejecuta con el valor a buscar. IMPORTANTE: usar el parametro y NO leer el
   * valor del estado del componente.
   *
   * El escaneo por camara llama a setValue y a onSearch en el mismo tick, y
   * setState es asincrono: una funcion que lea del estado recibiria el valor
   * anterior y la busqueda no encontraria nada.
   */
  onSearch: (value: string) => void
  /** Bloquea el disparo, por ejemplo mientras una busqueda esta en vuelo. */
  disabled?: boolean
  /** Valor inicial del input. */
  initialValue?: string
  /**
   * Trimea en cada tecla. Default true, que es como venia ScanStep.
   * Ojo: con true no se pueden escribir espacios en el input.
   */
  trimOnChange?: boolean
  /**
   * Ref externa al input. Si no se pasa, el hook usa una interna.
   * Sirve para los componentes que ya exponen su propia ref al consumidor
   * (por ejemplo ScanSearchPanel, que la usa para reenfocar desde afuera).
   */
  inputRef?: React.RefObject<HTMLInputElement>
}

/**
 * Centraliza el cableado de un input de escaneo del WMS: lector fisico (HID que
 * escribe + Enter), tipeo manual y lectura por camara, los tres disparando la
 * misma busqueda.
 *
 * Existe para que cada pantalla no tenga que repetir los handlers ni recordar
 * los dos detalles que son faciles de romper: pasar el valor por parametro y
 * devolver el foco al input cuando se cierra el dialogo de la camara.
 */
export const useScanInput = ({
  onSearch,
  disabled = false,
  initialValue = '',
  trimOnChange = true,
  inputRef: externalRef
}: UseScanInputArgs) => {
  const [value, setValue] = useState(initialValue)
  const internalRef = useRef<HTMLInputElement | null>(null)
  const inputRef = externalRef ?? internalRef

  // En modo camara no se enfoca el input, para no abrir el teclado virtual.
  const { autoFocus } = useScanKeyboard()

  // Va por ref para que una arrow inline en el call site no invalide los
  // handlers memoizados en cada render.
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch

  const disabledRef = useRef(disabled)
  disabledRef.current = disabled

  const search = useCallback((rawValue: string) => {
    if (disabledRef.current) return
    const trimmed = rawValue.trim()
    if (!trimmed) return
    onSearchRef.current(trimmed)
  }, [])

  /** Dispara la busqueda con lo que hay en el input (boton "Siguiente"/"Buscar"). */
  const submit = useCallback(() => search(value), [search, value])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(trimOnChange ? e.target.value.trim() : e.target.value)
    },
    [trimOnChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') search(value)
    },
    [search, value]
  )

  /** Deja el valor leido en el input y busca con el valor fresco, no con el del estado. */
  const handleCameraScan = useCallback(
    (scanned: string) => {
      setValue(scanned)
      search(scanned)
    },
    [search]
  )

  /**
   * Al cerrar el dialogo, MUI devuelve el foco al boton que lo abrio y no al
   * input. Sin esto, quien abre y cierra la camara y despues usa el lector
   * fisico escribiria fuera del campo.
   */
  const handleCameraCancel = useCallback(() => {
    // En modo camara no se reenfoca: seria otra via para abrir el teclado.
    if (!autoFocus) return
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [autoFocus])

  const reset = useCallback(() => setValue(''), [])

  return {
    value,
    setValue,
    reset,
    inputRef,
    submit,
    /**
     * false en modo camara. Se expone ademas de ir en fieldProps para que las
     * pantallas puedan guardar sus propios `focus()` programaticos, que son otra
     * via por la que se abre el teclado virtual.
     */
    autoFocus,
    /**
     * Spread en el TextField del escaneo.
     *
     * Se llama fieldProps y no inputProps a proposito: MUI ya tiene un prop
     * `inputProps` (props del <input> interno) y un `InputProps`, y tener un
     * tercer nombre parecido en el mismo JSX se lee mal.
     */
    fieldProps: {
      value,
      inputRef,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      /**
       * false en modo camara: enfocar abriria el teclado virtual. Los call
       * sites que traen su propio `autoFocus` tienen que sacarlo, o pisan este
       * segun el orden del spread.
       */
      autoFocus
    },
    /** Spread en el CameraScanButton. */
    cameraProps: {
      onScan: handleCameraScan,
      onCancel: handleCameraCancel,
      // Es el campo que se resalta en mobile mientras es el destino del escaneo.
      inputRef
    }
  }
}
